import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  ShoppingCart, 
  Building2, 
  Package, 
  TrendingUp, 
  ArrowRight,
  RefreshCw,
  Truck,
  ScanLine,
  Calculator,
  Wallet,
  FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PurchasingPage() {
  const [stats, setStats] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, rateRes] = await Promise.all([
        axios.get(`${API}/purchasing/stats`),
        axios.get(`${API}/purchasing/exchange-rate`)
      ]);
      setStats(statsRes.data);
      setExchangeRate(rateRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Kunde inte hämta data");
    } finally {
      setLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    if (!value) return "0 kr";
    return new Intl.NumberFormat('sv-SE', { 
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value) + ' kr';
  };

  const menuItems = [
    {
      title: "Leverantörer",
      description: "Hantera leverantörer och kontaktuppgifter",
      icon: Building2,
      link: "/purchasing/suppliers",
      color: "text-blue-500",
      bgColor: "bg-blue-500/10"
    },
    {
      title: "Inköpsordrar",
      description: "Hantera beställningar och förfrågningar",
      icon: ShoppingCart,
      link: "/purchasing/orders",
      color: "text-purple-500",
      bgColor: "bg-purple-500/10"
    },
    {
      title: "Prislistor",
      description: "Hantera inköpspriser per leverantör",
      icon: TrendingUp,
      link: "/purchasing/prices",
      color: "text-teal-500",
      bgColor: "bg-teal-500/10"
    },
    {
      title: "Räkna Offert",
      description: "Beräkna offerter med produkter från lagret",
      icon: Calculator,
      link: "/purchasing/quote",
      color: "text-amber-500",
      bgColor: "bg-amber-500/10"
    },
    {
      title: "Skanna faktura",
      description: "Skanna faktura med AI och se mottagna ordrar",
      icon: ScanLine,
      link: "/purchasing/scan",
      color: "text-orange-500",
      bgColor: "bg-orange-500/10"
    }
  ];

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navigation />
      
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <ShoppingCart className="h-6 w-6 text-primary" />
            <h1 className="font-heading text-2xl font-bold uppercase tracking-tight" data-testid="purchasing-title">
              Inköp
            </h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Hantera leverantörer, prislistor och beställningar
          </p>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Ordervärde */}
          <Card>
            <CardContent className="p-4 text-center">
              <Wallet className="h-8 w-8 mx-auto mb-2 text-primary" />
              <p className="text-2xl font-bold font-heading">{formatCurrency(stats?.total_order_value)}</p>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Ordervärde</p>
            </CardContent>
          </Card>
          
          {/* Produkter med pris */}
          <Card>
            <CardContent className="p-4 text-center">
              <Package className="h-8 w-8 mx-auto mb-2 text-green-500" />
              <p className="text-2xl font-bold font-heading">{stats?.unique_products_with_prices || 0}</p>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Produkter med pris</p>
            </CardContent>
          </Card>
          
          {/* På väg in */}
          <Card>
            <CardContent className="p-4 text-center">
              <Truck className="h-8 w-8 mx-auto mb-2 text-yellow-500" />
              <p className="text-2xl font-bold font-heading">{stats?.items_on_way || 0}</p>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">På väg in</p>
            </CardContent>
          </Card>
          
          {/* Valutakurs */}
          <Card>
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center gap-1 mb-2">
                <span className="text-lg">€</span>
                <ArrowRight className="h-4 w-4" />
                <span className="text-lg">kr</span>
              </div>
              <p className="text-2xl font-bold font-heading">
                {exchangeRate?.rate?.toFixed(2) || "—"}
              </p>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">EUR → SEK</p>
              <Button 
                variant="ghost" 
                size="sm" 
                className="mt-2 text-xs h-7 px-2 text-muted-foreground hover:text-primary"
                onClick={fetchData}
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Uppdatera
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Menu Items */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {menuItems.map((item) => (
            <Card 
              key={item.title} 
              className="relative overflow-hidden transition-all hover:border-primary/50 cursor-pointer"
            >
              <Link to={item.link}>
                <CardHeader className="pb-2">
                  <div className={`w-12 h-12 rounded-lg ${item.bgColor} flex items-center justify-center mb-2`}>
                    <item.icon className={`h-6 w-6 ${item.color}`} />
                  </div>
                  <CardTitle className="font-heading text-lg uppercase">{item.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                  <div className="flex items-center gap-1 mt-4 text-primary text-sm font-medium">
                    Öppna <ArrowRight className="h-4 w-4" />
                  </div>
                </CardContent>
              </Link>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
}
